import type { ReactNode } from 'react';
import type { RoomId } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import type { ChatApi } from '../../state/useChat';

/**
 * The rooms this tab is actually holding a socket to, across the top.
 *
 * One or two, never more, because `LIMITS.open` is two: the room you call
 * home and the room of the level you are standing in. It exists because of the
 * single most anti-community thing this product used to do — walking into a
 * level threw you out of the conversation you were in. Now the level's room is
 * *added*, and this is where the other one stays visible while you are in it.
 *
 * The shape is the argument. With one room open it renders its child and
 * nothing else: no wrapper, no tab, no dot, so a learner who never opens a
 * level sees the header they have always seen, down to the pixel. The second
 * room is the only thing that puts a strip on the screen, and it appears the
 * moment there is a choice to make.
 *
 * The room on screen is not drawn as a tab. It is the child — the room picker,
 * which is already a disclosure button carrying the room's name and already
 * the way to a different room. Drawing it twice, once as a tab and once as the
 * picker, would spend a third of a 280px header saying the same word twice;
 * and a tab that was the picker as well would be a control that changes rooms
 * when you press it and opens a menu when you press it, which is two answers
 * to one gesture. So: the active room is the picker, and every other open room
 * is a tab beside it.
 *
 * There is no arrow-key roving. There are at most two controls here, they are
 * ordinary buttons, and Tab reaches both in the order they are drawn; a roving
 * tabindex over two items is ceremony that buys a keyboard user nothing and
 * costs them the one thing they can already rely on.
 *
 * Not `role="tablist"`, for the same reason. These are not tabs in the ARIA
 * sense — there is no set of panels swapping, there is one panel showing a
 * different room — and the room that *is* showing is never one of these
 * buttons anyway, so there is nothing here for `aria-selected` to be true of.
 * A group with a name, holding ordinary buttons that each say which room they
 * go to and what is waiting there, is the honest description.
 */

/**
 * One open room as the hook reports it. Reached through `ChatApi` rather than
 * imported by name so that the hook is free to move the declaration without
 * every component here caring where it went.
 */
type RoomView = ChatApi['rooms'][number];

export interface RoomStripProps {
  /** Every room this tab holds, home first. One or two. */
  rooms: RoomView[];
  /** The one on screen. */
  active: RoomId;
  /** Show another of them. Reconnects nothing — both sockets are already open. */
  onShow(room: RoomId): void;
  /**
   * What one room is called on screen.
   *
   * A function rather than the protocol's `roomLabel` called here, because a
   * team room is named whatever its members named it and that lives in D1 —
   * the protocol answers the generic word and the panel, which holds the
   * roster, substitutes the real one (web/src/lib/teams.ts).
   */
  label(room: RoomId): string;
  /** The picker for the room on screen. */
  children: ReactNode;
}

export function RoomStrip({ rooms, active, onShow, label, children }: RoomStripProps) {
  const s = useStrings().chat;
  const others = rooms.filter((r) => r.room !== active);

  // One room, or a room list this tab has not filled yet: the header as it
  // has always been.
  if (others.length === 0) return <>{children}</>;

  return (
    <div className="chat-strip" role="group" aria-label={s.strip.label}>
      {children}
      {others.map((r) => (
        <button
          key={r.room}
          type="button"
          className="chat-strip-tab"
          aria-label={s.strip.tab(r.room, r.unread, r.mentions)}
          title={s.strip.why}
          onClick={() => onShow(r.room)}
        >
          <span className="label">{label(r.room)}</span>
          {/* The same news for an eye. The accessible name above already says
              it in words, so this is hidden from a reader rather than read
              out twice; past ninety-nine the number stops being a number and
              becomes "a lot", which is all anybody does with it. */}
          {r.unread > 0 && (
            <span className={r.mentions > 0 ? 'chat-strip-dot is-mention' : 'chat-strip-dot'} aria-hidden="true">
              {r.unread > 99 ? '99+' : r.unread}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export default RoomStrip;
